import MP4Box from 'mp4box';
import axios from 'axios';
import audioHelper from './audio-helper';
import auth from 'auth';
import Data, { splitDataUri } from 'app-core/Data';
import { postDurableFunction, getSasForAzureContainer, uploadBlobWithInfoResponse, deleteBlob } from 'app-core/azure';
import config from 'app-core/config';

if (__DEBUG__)
	window.mp4box = MP4Box;

async function getVideoThumbnails(track, opts) {
	if (!track?.__base64) return [];

	const canvas = document.createElement('canvas');
	let ctx = canvas.getContext('2d');
	let frameSize = 100;

	canvas.width = frameSize;
	canvas.height = track.__canvasHeight;

	const start = opts?.start || 0;
	const duration = (opts?.end - start) || track.duration;
	const width = opts?.width || track.__canvasWidth;

	const timeDelta = duration / (width / frameSize);
	const video = document.createElement('video');


	let isProcessed = false;
	let promiseRes = null;
	let results = [];
	const process = async () => {
		if (isProcessed) return;
		isProcessed = true;
		let curFrame = 0;
		let curTime = curFrame * timeDelta + start;
		while (curTime < (start + duration)) {
			let thmb = track.thumbnails.find(f => f.time > curTime - timeDelta / 2 && f.time < curTime + timeDelta / 2);
			if (!thmb) {
				if (__DEBUG__)
					console.debug('Generate thumbnail');
				video.currentTime = curTime + timeDelta / 2;
				await (new Promise(res => promiseRes = res));
				ctx.fillRect(0, 0, frameSize, track.__canvasHeight);
				ctx.drawImage(video, 0, 0, frameSize, track.__canvasHeight);
				thmb = {
					time: curTime,
					base64: canvas.toDataURL('image/jpeg', 0.8)
				};
				track.thumbnails.push(thmb);
			}
			curFrame += 1;
			curTime = curFrame * timeDelta + start;
			results.push(thmb);
		}

		if (__DEBUG__)
			console.debug('Generate thumbnails', { opts, width, duration, results });

		return results;
	}
	
	const result = process();
	video.oncanplaythrough = () => {
		if (promiseRes)
			promiseRes();
	};
	video.onerror = () => {
		if (promiseRes)
			promiseRes();
	}

	video.src = track.__base64;

	return result;
}

function buildVideo(FILE_URL, callback, opts) {
	const audioEncodingType = 'opus';
	const handleAudioEncoding = false;

	let FPS = 25;
	const ONE_SECOND_IN_MICROSECOND = 1000000;
	let BITRATE = 700000;
	let MICROSECONDS_PER_FRAME = ONE_SECOND_IN_MICROSECOND / FPS;
	const SAMPLE_RATE = 44100;

	// const BUFFER_LENGTH = MICROSECONDS_PER_FRAME / ONE_SECOND_IN_MICROSECOND * SAMPLE_RATE;

	let muxStarted = false;

	// MP4BOX nbSample limits the maximum number of nbSamples
	const nbSampleMax = 30;
	let nbSampleTotal = 0;
	let countSample = 0;
	let file = null;
	let waitingFrame = false;
	let stopped = true;
	let videoTrack = null;
	let audioTrack = null;
	let outputFile = null;

	let videoDuration = 0;
	let videoDecoder = null;
	const videoFrames = [];
	let videoNbSample;
	let decodedVideoFrameCount = 0;
	let processingVideo = false;
	let videoFramerate;
	let videoW;
	let videoH;

	let audioDecoder = null;

	const decodedAudioFrames = [];
	let decodedAudioFrameCount = 0;
	let audioTotalTimestamp = 0;
	let processingAudio = false;

	let videoEncoder = null;
	// difference between the last read frame and the last encoded frame
	// The process needs to be slowed down to avoid saturation of the VideoEncoder
	const encodingFrameDistance = 5;

	let waitingVideoReading = false;
	let encodedVideoFrameCount = 0;
	let encodingVideoTrack = null;
	let videoFrameDurationInMicrosecond;
	// Changing the output video size
	const encodingVideoScale = 1;
	let outputW;
	let outputH;

	let audioEncoder = null;
	let waitingAudioReading = false;
	let encodingAudioTrack = null;
	let encodedAudioFrameCount = 0;
	let totalaudioEncodeCount = 0;

	const output = document.createElement('canvas');
	output.width = outputW;
	output.height = outputH;

	outputFile = MP4Box.createFile();

	if (__DEBUG__)
		window.outputFile = outputFile;

	const freeMemory = () => {
		if (processingVideo) {
			if (decodedVideoFrameCount > 0) {
				file.releaseUsedSamples(videoTrack.id, decodedVideoFrameCount - 1);
			}
			return;
		}
		if (processingAudio) {
			if (decodedAudioFrameCount > 0) {
				file.releaseUsedSamples(audioTrack.id, decodedAudioFrameCount - 1);
			}
		}
	};

	const onVideoDemuxingComplete = () => {
		videoDecoder.close();

		processingVideo = false;

		if (audioTrack && handleAudioEncoding) {
			setupAudioEncoder({
				// codec: audioTrack.codec, // AudioEncoder does not support this field
				codec: audioEncodingType,
				sampleRate: audioTrack.audio.sample_rate,
				numberOfChannels: audioTrack.audio.channel_count,
				bitrate: audioTrack.bitrate,
			});

			setupAudioDecoder({
				codec: audioTrack.codec,
				sampleRate: audioTrack.audio.sample_rate,
				numberOfChannels: audioTrack.audio.channel_count,
			});

			getNextSampleArray();
		}
	};

	const saveFile = () => {
		if (callback)
			callback({
				video: {
					buffer: outputFile.getBuffer(),
					duration: videoDuration / 1000
				}
			});
		output.remove();
		//outputFile.save('test.mp4');
	};

	const onAudioDemuxingComplete = () => {
		audioDecoder.close();
	};

	const onAudioEncodingComplete = () => {
		audioEncoder.close();
		saveFile();
	};

	const onVideoEncodingComplete = () => {
		videoEncoder.close();

		if (!audioTrack || !handleAudioEncoding) saveFile();
	};

	const continueReading = () => {
		if (processingVideo) {
			waitingVideoReading = decodedVideoFrameCount - encodedVideoFrameCount > encodingFrameDistance;

			if (waitingVideoReading === false) {
				readNextFrame();
			} else {
				// console.log('waiting videoEncoder');
			}

			return;
		}

		if (processingAudio) {
			waitingAudioReading = decodedAudioFrameCount - encodedAudioFrameCount > encodingFrameDistance;

			if (waitingAudioReading === false) {
				readNextFrame();
			} else {
				// console.log('waiting audioEncoder');
			}
		}
	};

	const onVideoFrameReadyToUse = (imageBitmap) => {
		createImageBitmap(imageBitmap, 0, 0, videoW, videoH, { resizeWidth: outputW, resizeHeight: outputH, resizeQuality: 'high' }).then((bmp) => {
			//ctx.drawImage(bmp, 0, 0);

			const timestamp = videoFrameDurationInMicrosecond * decodedVideoFrameCount;

			if (opts?.onVideoFrameReady) {
				opts?.onVideoFrameReady({ bmp, timestamp, frameNumber: decodedVideoFrameCount, fps: FPS });
			}

			const videoFrame = new window.VideoFrame(bmp, { timestamp, duration: videoFrameDurationInMicrosecond });
			videoEncoder.encode(videoFrame);
			videoFrame.close();
			bmp.close();

			decodedVideoFrameCount++;

			if (decodedVideoFrameCount === nbSampleTotal) {
				onVideoDemuxingComplete();
			} else {
				continueReading();
			}
		});

		imageBitmap.close();
	};

	const onAudioFrameReadyToUse = (audioFrame) => {
		/*
			//just an example to expose how to get audio-sample-buffers from AudioData object :
			//
			//let leftChannel = new Float32Array(audioSampleLength * 4);
			//audioFrame.copyTo(leftChannel,leftChannelOptions);
			//
			//let rightChannel = leftChannel;
			//if(audioFrame.numberOfChannels > 1){
			//		rightChannel = new Float32Array(audioSampleLength * 4);
			//		audioFrame.copyTo(rightChannel,rightChannelOptions);
			//}
			*/

		audioEncoder.encode(audioFrame);
		audioFrame.close();

		decodedAudioFrameCount++;

		if (decodedAudioFrameCount === nbSampleTotal) {
			onAudioDemuxingComplete();
		} else {
			readNextFrame();
		}
	};

	let getNextSampleArray = () => {
		if (!stopped || !muxStarted || (processingVideo && countSample === nbSampleTotal)) {
			return;
		}

		stopped = false;
		file.start();
	};

	let readNextFrame = () => {
		if (processingVideo) {
			if (videoFrames.length === 0) {
				if (decodedVideoFrameCount > 0) {
					if (!waitingFrame) {
						waitingFrame = true;
						getNextSampleArray();
					}
				}
			} else {
				onVideoFrameReadyToUse(videoFrames.shift());
			}
			return;
		}

		if (processingAudio) {
			if (decodedAudioFrames.length === 0) {
				if (decodedAudioFrameCount > 0) {
					if (!waitingFrame) {
						waitingFrame = true;
						getNextSampleArray();
					}
				}
			} else {
				onAudioFrameReadyToUse(decodedAudioFrames.shift());
			}
		}
	};

	const setupVideoEncoder = (config) => {
		const videoEncodingTrackOptions = {
			timescale: ONE_SECOND_IN_MICROSECOND,
			width: outputW,
			height: outputH,
			nb_samples: videoNbSample,
			media_duration: videoNbSample * 1000 / FPS,
			brands: ['isom', 'iso2', 'avc1', 'mp41'],
			avcDecoderConfigRecord: null,
			duration: videoTrack?.duration / videoTrack?.timescale * ONE_SECOND_IN_MICROSECOND
		};

		const videoEncodingSampleOptions = {
			duration: videoFrameDurationInMicrosecond,
			dts: 0,
			cts: 0,
			is_sync: false,
		};

		videoEncoder = new window.VideoEncoder({
			output: (encodedChunk, config) => {
				if (encodingVideoTrack == null) {
					videoEncodingTrackOptions.avcDecoderConfigRecord = config.decoderConfig.description;
					encodingVideoTrack = outputFile.addTrack(videoEncodingTrackOptions);
				}

				const buffer = new ArrayBuffer(encodedChunk.byteLength);
				encodedChunk.copyTo(buffer);

				videoEncodingSampleOptions.dts = encodedVideoFrameCount * MICROSECONDS_PER_FRAME;
				videoEncodingSampleOptions.cts = encodedVideoFrameCount * MICROSECONDS_PER_FRAME;
				videoEncodingSampleOptions.is_sync = encodedChunk.type === 'key';

				outputFile.addSample(encodingVideoTrack, buffer, videoEncodingSampleOptions);

				encodedVideoFrameCount++;

				if (encodedVideoFrameCount === videoNbSample) {
					onVideoEncodingComplete();
				} else if (waitingVideoReading) {
					continueReading();
				}
			},
			error: (err) => {
				console.error('VideoEncoder error : ', err, { config });
			},

		});

		if (__DEBUG__)
			console.debug('Setup video encoder', { config });

		videoEncoder.configure(config);
	};

	let setupAudioEncoder = (config) => {
		const audioEncodingTrackOptions = {
			timescale: SAMPLE_RATE,
			media_duration: 0,
			duration: 0,
			nb_samples: 0,
			samplerate: SAMPLE_RATE,
			width: 0,
			height: 0,
			hdlr: 'soun',
			name: 'SoundHandler',
			type: audioEncodingType,
		};

		const audioEncodingSampleOptions = {
			duration: 0,
			dts: 0,
			cts: 0,
			is_sync: false,
		};

		audioEncoder = new window.AudioEncoder({
			output: (encodedChunk, config) => {
				if (encodingAudioTrack === null) {
					// The number of times audioEncoder.encode is triggered does not match the number of times AudioEncoder.output is triggered
					// And no API was found for aligning the two segments, so I had to manually calculate an inexact value
					// https://github.com/w3c/webcodecs/issues/240
					totalaudioEncodeCount = Math.floor(audioTotalTimestamp / encodedChunk.duration);
					audioEncodingTrackOptions.nb_samples = totalaudioEncodeCount;
					const trackDuration = audioTotalTimestamp / ONE_SECOND_IN_MICROSECOND;
					// tkhd.dutation: 33600 (00:00:00.700) timescales * seconds
					audioEncodingTrackOptions.duration = trackDuration * SAMPLE_RATE;

					// mvhd.duration: 33600 (00:00:00.700) timescales * seconds
					audioEncodingTrackOptions.media_duration = trackDuration * SAMPLE_RATE;

					// TODO: check if correct
					// audioEncodingTrackOptions.description = config.decoderConfig.description;

					encodingAudioTrack = outputFile.addTrack(audioEncodingTrackOptions);
				}

				const buffer = new ArrayBuffer(encodedChunk.byteLength);
				encodedChunk.copyTo(buffer);

				const sampleDuration = encodedChunk.duration / ONE_SECOND_IN_MICROSECOND * SAMPLE_RATE;

				audioEncodingSampleOptions.dts = encodedAudioFrameCount * sampleDuration;
				audioEncodingSampleOptions.cts = encodedAudioFrameCount * sampleDuration;
				audioEncodingSampleOptions.duration = sampleDuration;
				audioEncodingSampleOptions.is_sync = encodedChunk.type === 'key';

				outputFile.addSample(encodingAudioTrack, buffer, audioEncodingSampleOptions);

				encodedAudioFrameCount++;

				if (encodedAudioFrameCount >= totalaudioEncodeCount) {
					onAudioEncodingComplete();
				} else if (waitingAudioReading) {
					continueReading();
				}
			},
			error: (err) => {
				console.error('AudioEncoder error : ', err);
			},
		});

		audioEncoder.configure(config);
	};

	const getExtradata = (file, videoTrack) => {
		let description;
		const trak = file.getTrackById(videoTrack.id);
		for (const entry of trak.mdia.minf.stbl.stsd.entries) {
			if (entry.avcC || entry.hvcC) {
				const stream = new MP4Box.DataStream(undefined, 0, MP4Box.DataStream.BIG_ENDIAN);
				if (entry.avcC) {
					entry.avcC.write(stream);
				} else {
					entry.hvcC.write(stream);
				}
				description = new Uint8Array(stream.buffer, 8); // Remove the box header.
				break;
			}
		}
		return description;
	};

	const setupVideoDecoder = (config) => {
		let timeout = null;

		processingVideo = true;
		waitingFrame = true;
		countSample = 0;
		nbSampleTotal = videoTrack.nb_samples;

		output.width = outputW;
		output.height = outputH;

		videoDecoder = new window.VideoDecoder({
			output: (videoFrame) => {
				createImageBitmap(videoFrame).then((img) => {
					videoFrames.push(img);
					videoFrame.close();
					freeMemory();

					// I use a timeout to give some time to videoDecoder to provide a bunch of frames
					// The idea behind this is to maintain a small amount of active frames in memory.
					//
					//= > videoDecoder output frame by frame but I can't compare the amount of
					// frame decoded by VideoDecoder and the amount of frame sent to VideoDecoder
					// because the first bunch of frames released by VideoDecoder is always smaller than
					// the amount of frame sent.
					//
					// the amount of frames released at the beginning depends of the video file itself, it's not
					// a fixed value.
					//= ==> the difference will be released at the end of the decoding using VideoDecoder.flush
					//
					// Without setTimeout, the process start again once I got a single frame.
					//= > I extract new frames once the array "videoFrames" is empty, so if i continue the process
					// immediatly after I got a single frame, the array become empty almost immediatly and the extraction
					// provide a lot of frame too early compared to the real progress of the video-reading

					if (timeout) clearTimeout(timeout);
					timeout = setTimeout(() => {
						if (waitingFrame) {
							waitingFrame = false;
							continueReading();
						}
					}, 15);
				});
			},
			error: (e) => {
				console.error('VideoDecoder error : ', e);
			},
		});

		if (__DEBUG__)
			console.debug('Video decode', { config });

		videoDecoder.configure(config);
		file.setExtractionOptions(videoTrack.id, null, { nbSamples: nbSampleMax });
	};

	const setupAudioDecoder = (config) => {
		let timeout = null;
		countSample = 0;
		processingAudio = true;
		waitingFrame = true;
		nbSampleTotal = audioTrack.nb_samples;

		audioDecoder = new window.AudioDecoder({
			output: (audioFrame) => {
				decodedAudioFrames.push(audioFrame);

				if (timeout) {
					clearTimeout(timeout);
				}
				timeout = setTimeout(() => {
					if (waitingFrame) {
						waitingFrame = false;
						readNextFrame();
					}
				}, 15);
			},
			error: (err) => {
				console.error('AudioDecoder error : ', err, config);
			},
		});

		audioDecoder.configure(config);

		file.setExtractionOptions(audioTrack.id, null, { nbSamples: nbSampleMax });
	};

	const loadFile = (url) => {
		file = MP4Box.createFile();

		file.onerror = (e) => {
			console.error('file onerror ', e);
		};

		file.onError = (e) => {
			console.error('MP4Box file error => ', e);
		};
		file.onReady = (info) => {
			muxStarted = true;
			videoTrack = info.videoTracks[0];
			audioTrack = info.audioTracks[0];

			BITRATE = videoTrack?.bitrate || BITRATE;
			FPS = (videoTrack?.nb_samples * videoTrack?.timescale / videoTrack?.samples_duration) || FPS;
			MICROSECONDS_PER_FRAME = ONE_SECOND_IN_MICROSECOND / FPS;

			if (__DEBUG__) {
				console.debug('File loaded', { info, FPS })
			}

			if (audioTrack) {
				// audioSamplerate = audioTrack.audio.sample_rate;
				// audioChannelCount = audioTrack.audio.channel_count;
				// audioNbSample = audioTrack.nb_samples;
				audioTotalTimestamp = audioTrack.samples_duration / audioTrack.audio.sample_rate * ONE_SECOND_IN_MICROSECOND;
			}
			videoNbSample = videoTrack.nb_samples;
			// TODO: There are some videos where videoTrack.movie_duration does not get the correct duration
			videoDuration = (info.duration / info.timescale) * 1000;

			// TODO: Using Math.ceil will result in inaccurate frames
			videoFramerate = Math.ceil(1000 / (videoDuration / videoTrack.nb_samples));
			videoFrameDurationInMicrosecond = ONE_SECOND_IN_MICROSECOND / videoFramerate;

			videoW = videoTrack.track_width;
			videoH = videoTrack.track_height;

			outputW = videoW * encodingVideoScale;
			outputH = videoH * encodingVideoScale;

			if (opts?.maxWidth) {
				const coef = opts?.maxWidth / videoW;
				if (coef < 1) {
					outputW = opts?.maxWidth;
					outputH = parseInt(videoH * coef);
				}
			}

			if (opts?.maxHeight) {
				const coef = opts?.maxHeight / videoH;
				if (coef < 1) {
					outputH = opts?.maxHeight;
					outputW = parseInt(videoW * coef);
				}
			}

			outputW += outputW % 2;
			outputH += outputH % 2;

			if (__DEBUG__)
				console.debug('Video processing', { outputW, outputH, opts });

			setupVideoEncoder({
				codec: 'avc1.4d0034' || videoTrack?.codec,
				width: outputW,
				height: outputH,
				hardwareAcceleration: 'prefer-hardware',
				framerate: videoFramerate < 120 ? videoFramerate : 24,
				bitrate: BITRATE,
				avc: { format: 'avc' },
			});

			setupVideoDecoder({
				codec: videoTrack.codec,
				codedWidth: videoW,
				codedHeight: videoH,
				description: getExtradata(file, videoTrack,),
			});

			onVideoReadyToPlay(); // getNextSampleArray() => file.start()
		};

		file.onSamples = (trackId, ref, samples) => {
			// To save memory, process the dumux-step little by little
			// so stop reading the file between the two demux-processes

			if (videoTrack.id === trackId) {
				stopped = true;
				file.stop();

				countSample += samples.length;

				for (const sample of samples) {
					const type = sample.is_sync ? 'key' : 'delta';

					const chunk = new window.EncodedVideoChunk({
						type,
						timestamp: sample.cts,
						duration: sample.duration,
						data: sample.data,
					});

					videoDecoder.decode(chunk);
				}

				if (countSample === nbSampleTotal) {
					videoDecoder.flush();
				}

				return;
			}

			if (audioTrack.id === trackId) {
				stopped = true;
				file.stop();
				countSample += samples.length;

				for (const sample of samples) {
					const type = sample.is_sync ? 'key' : 'delta';

					const chunk = new window.EncodedAudioChunk({
						type,
						timestamp: sample.cts,
						duration: sample.duration,
						data: sample.data,
						offset: sample.offset,
					});

					audioDecoder.decode(chunk);
				}

				if (countSample === nbSampleTotal) {
					audioDecoder.flush();
				}
			}
		};

		fetch(url).then((response) => {
			let offset = 0;
			let buf;
			const reader = response.body.getReader();

			const push = () => reader.read().then(({ done, value }) => {
				if (done === true) {
					file.flush(); // will trigle file.onReady
					return;
				}

				buf = value.buffer;
				buf.fileStart = offset;
				offset += buf.byteLength;
				file.appendBuffer(buf);
				push();
			}).catch((e) => {
				console.error('reader error ', e);
			});
			push();
		});
	};

	let onVideoReadyToPlay = () => {
		getNextSampleArray();
	};

	function main() {
		loadFile(FILE_URL);
	}

	main();
}

function extractAudio(url) {
	return new Promise(res => {
		return axios.get(url, {
			responseType: 'blob'
		}).then(response => {
			let fr = new FileReader();
			fr.onloadend = (evt) => {
				const context = new AudioContext();
				return context.decodeAudioData(evt.target.result, (buffer) => {
					let sampleRate = 48000;
					let offlineAudioCtx = new OfflineAudioContext({
						numberOfChannels: 2,
						length: sampleRate * buffer.duration,
						sampleRate
					});

					let source = offlineAudioCtx.createBufferSource();
					source.buffer = buffer;
					source.connect(offlineAudioCtx.destination);
					source.start();

					offlineAudioCtx.startRendering().then((renderedBuffer) => {
						res({
							buffer: renderedBuffer,
							wav: audioHelper.bufferToWave(renderedBuffer),
							duration: renderedBuffer?.duration
						});
					});
				}).catch(() => res(null));
			};
			fr.onerror = () => res(null);
			fr.readAsArrayBuffer(response.data);
		}).catch(res => res(null));
	});
}

function extractVideo(url) {
	return new Promise(res => {
		try {
			buildVideo(url, data => res(data?.video));
		} catch {
			res(null);
		}
	});
}

async function splitVideoInBrowser(url) {
	const results = await Promise.all([
		extractAudio(url),
		extractVideo(url)
	]);

	return {
		audio: results[0],
		video: results[1]
	}
}

function getVideoDuration(src) {
	return new Promise(res => {
		var video = document.createElement('video');
		video.preload = 'metadata';
		video.onloadedmetadata = function() {
			window.URL.revokeObjectURL(video.src);
			res(video.duration);
		}
		video.onerror = function() { res(0); }
		video.src = src;
	});
}

async function splitVideo(base64, fileName, asBlob) {
	const base64Content = (await splitDataUri(base64))?.base64;
	const azureSettings = await Data.getAzureSettings();

	const blob = await Data.base64toBlob(base64Content, 'video/mpeg');

	const name = `${fileName || ''}_${Data.uuid()}.mp4`;
	const azureBlob = await uploadBlobWithInfoResponse(name, blob, 'temporary');
	
	const sas = (await getSasForAzureContainer('temporary', 300))?.sasKey;
	const azureUrl = azureBlob?.AzureFileUrl + `?${sas}`;

	const prepareAzureContainer = (res) => {
		return {
			StorageType: 2,
			AzureAccountOid: azureSettings.StorageAccount.Oid,
			AzureContainer: res?.azureFileInfo?.container,
			AzureFileUrl: `${azureSettings.StorageAccount.BlobEndPoint}${res?.azureFileInfo?.container}/${res?.azureFileInfo?.fileName}`,
			FileName: res?.azureFileInfo?.fileName,
			FileSize: res?.size
		}
	}

	let audio = null;
	let video = null;

	if (__DEBUG__) console.time('Split video');

	await Data.postAudioApi('ffmpeg/execute', {
		Command: '-i [SampleVideo.mp4] -map 0:a [out_a.mp3] -map 0:v [out_v.mp4]',
		OutputFiles: ['out_a.mp3', 'out_v.mp4'],
		SaveToAzure: !asBlob,
		SaveToAzureContainer: '', //If empty then ouput file will be loaded into default base audio container
		InputFiles: [
			{
				FileName: 'SampleVideo.mp4',
				URL: azureUrl
			}
		]
	}).then(data => {
		const files = data?.outputFiles || [];
		if (!files?.length) throw new Error(config.getVueApp().$tCaption('Errors occured during operation.', '@Creative'))
		if (__DEBUG__)
			console.debug('Split video azure result', { data, azureUrl });
		const audioResult = files.find(f => f.fileName === 'out_a.mp3');
		const videoResult = files.find(f => f.fileName === 'out_v.mp4');

		audio = asBlob ? `data:audio/mp3;base64,${audioResult?.base64Content}` : prepareAzureContainer(audioResult);
		video = asBlob ? `data:video/mp4;base64,${videoResult?.base64Content}` : prepareAzureContainer(videoResult);
	}).finally(() => {
		deleteBlob(azureBlob?.FileName, azureBlob?.AzureContainer);
	});

	if (__DEBUG__) console.timeEnd('Split video');

	return {
		audio,
		video
	}
}


export default {
	defineVideoProperties(track) {
		if (track.type === 'video' && !track.hasOwnProperty('__getVideoThumbnails')) {
			const getVideoThumbnailsFunc = () => getVideoThumbnails(track);
			Object.defineProperty(track, '__getVideoThumbnails', {
				get: () => getVideoThumbnailsFunc
			});

			track.thumbnails = [];
			track.visibleThumbnails = [];
			track.__customCanvasDraw = async () => {
				if (!track.__isSrcLoaded) return;
				let showLoader = !track.visibleThumbnails.length;
				if (showLoader) track.__isLoading++;
				track.visibleThumbnails = await track.__getVideoThumbnails().finally(() => track.__isLoading--);
			}
		}
	},
	buildVideo,
	extractVideo,
	extractAudio,
	splitVideoInBrowser,
	splitVideo,
	getVideoDuration
};
